import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react"
import { useSelector } from "react-redux"
import { selectAuthState } from "@/store/features/authSlice"
import ProductList from "@/components/ProductList/v3/ProductList"
import { productListContextPropsType } from "@/propTypes/plp"
import { handleWindowScroll } from "@/utils/product"
import { selectGenericState } from "@/store/features/genericSlice"
import { selectProductListState } from "@/store/features/productListSlice"
import * as indexedDBUtil from "@/utils/indexDB"

// Createcontext
const ProductContext = createContext()

// Create custom hook for using the context
const useProductContext = () => {
  const context = useContext(ProductContext)
  if (!context) {
    throw new Error("useProductContext must be used within ProductProvider")
  }
  return context
}

const ProductListContext = ({
  children,
  data: componentProps,
  eventHandler,
  plpData: serverPlpData,
  plpBadgeList: serverBadgeList,
  disableCommerce,
  swatchUrl,
  language,
  general,
  socialMediaConfigs,
  plpServletPath,
  facetQuery,
  sortQuery,
  locale,
  skuPrepend,
  plpPresetConfig,
  productsCount,
  deviceType,
  quickViewAuthData,
  bundleProductCardLabel,
}) => {
  // Initialize state with server data
  const twitter = socialMediaConfigs?.myArrayList.find(socialMediaConfig =>
    socialMediaConfig.map.name.toLowerCase().includes("twitter")
  )
  const facebook = socialMediaConfigs?.myArrayList.find(socialMediaConfig =>
    socialMediaConfig.map.name.toLowerCase().includes("facebook")
  )
  const linkedin = socialMediaConfigs?.myArrayList.find(socialMediaConfig =>
    socialMediaConfig.map.name.toLowerCase().includes("linkedin")
  )
  // session is having data
  const [plpLocalState, setPlpLocalState] = useState({
    promoData: { promos: [], isUpdate: false },
    plpData: serverPlpData?.data,
    cardsData: serverPlpData?.transformedCards || [], // Use transformed cards if available
    plpBadgeList: serverBadgeList || [],
    componentProps,
    // Include core configuration props
    disableCommerce,
    swatchUrl,
    language,
    general,
    twitter,
    facebook,
    linkedin,
    plpServletPath,
    hoverColor: false,
    selectedColor: null,
    facets: serverPlpData?.facets,
    facetLabels: serverPlpData?.facetLabels,
    colorLabels: serverPlpData?.colors,
    plpTotal: serverPlpData?.total ?? 0,
    isGrouped: serverPlpData?.isGrouped,
    filterQuery: facetQuery ? decodeURI(facetQuery) : "",
    selectedFilterArray: [],
    selectSort: sortQuery ? decodeURI(sortQuery) : "",
    locale,
    skuPrepend,
    plpPresetConfig,
    defaultProductsCount: productsCount,
    isLoading: false,
  })

  useEffect(() => {
    const updatePlpLoader = async () => {
      const fromPDP = await indexedDBUtil.getItem("fromPDP")
      setPlpLocalState(prev => ({
        ...prev,
        isLoading: fromPDP ? true : prev.isLoading,
      }))
    }
    updatePlpLoader()
  }, [])

  // override plpLocalState if session exist with previous state
  useEffect(() => {
    // Scroll to SKU after DOM update
    requestAnimationFrame(async () => {
      const plpsku = (await indexedDBUtil.getItem("plpsku")) || ""

      handleWindowScroll(plpsku)
    })
  }, [])

  // Handle client-side updates
  const updateClientState = useCallback(updates => {
    setPlpLocalState(prev => ({
      ...prev,
      isServerData: false, // Mark as client-side data
      ...updates,
    }))
  }, [])

  // Move setPromoInfo to context
  const setPromoInfo = useCallback(promo => {
    setPlpLocalState(prev => {
      const promoDataNew = [...prev.promoData.promos]
      if (
        promo &&
        !promoDataNew.find(item => item.position === promo.position)
      ) {
        promoDataNew.push(promo)
      }
      return {
        ...prev,
        promoData: { promos: promoDataNew, isUpdate: true },
      }
    })
  }, [])

  // Redux selectors for client-side state

  const {
    isServerData,
    isFromVisualNav,
    filterQueryState,
    visualNavAppliedFilters,
  } = useSelector(selectProductListState)

  const {
    isAuth: isAuthenticated,
    access_token: accessToken,
    user,
  } = useSelector(selectAuthState)

  const {
    globalConfig: { internationalization: { currencySign = "$" } = {} } = {},
    featureFlags = {},
  } = useSelector(selectGenericState)

  useEffect(() => {
    if (isFromVisualNav === true && filterQueryState !== "NONE") {
      setPlpLocalState(prev => ({
        ...prev,
        filterQuery: decodeURI(filterQueryState),
      }))
    } else if (isFromVisualNav === false && filterQueryState === "NONE") {
      setPlpLocalState(prev => ({
        ...prev,
        filterQuery: "",
      }))
    }
  }, [filterQueryState])

  const contextValue = useMemo(
    () => ({
      // State Management
      plpLocalState,
      setPlpLocalState,
      updateClientState,

      // Server-side Data
      componentProps,

      // Configuration
      disableCommerce: plpLocalState.disableCommerce,
      swatchUrl: plpLocalState.swatchUrl,
      language: plpLocalState.language,
      general: plpLocalState.general,
      twitter: plpLocalState.twitter,
      facebook: plpLocalState.facebook,
      linkedin: plpLocalState.linkedin,
      plpServletPath: plpLocalState.plpServletPath,
      featureFlags: featureFlags,
      plpBadgeList: plpLocalState.plpBadgeList,
      skuPrepend: plpLocalState.skuPrepend,
      plpPresetConfig: plpLocalState.plpPresetConfig,

      // Event Handlers
      eventHandler,
      setPromoInfo,

      // Authentication
      isAuthenticated,
      accessToken,
      user,
      // generic state
      currencySign,
      deviceType,
      isServerData,
      isFromVisualNav,
      filterQueryState,
      visualNavAppliedFilters,
      quickViewAuthData,
      bundleProductCardLabel,
    }),
    [
      plpLocalState,
      setPlpLocalState,
      updateClientState,
      componentProps,
      eventHandler,
      setPromoInfo,
      isAuthenticated,
      accessToken,
      user,
      isFromVisualNav,
      filterQueryState,
    ]
  )

  return (
    <ProductContext.Provider value={contextValue}>
      <ProductList>{children}</ProductList>
    </ProductContext.Provider>
  )
}
ProductListContext.propTypes = productListContextPropsType

export { useProductContext }
export default ProductListContext
